import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import { auth, db, uploadImage } from "../firebase"; // Asegúrate de importar tus configuraciones de Firebase
import {
  doc,
  getDoc,
  addDoc,
  setDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import Marcas from "./marcas";
import EmptyState from "./EmptyState";
import useMembership from "./useMembership";
import { MEMBERSHIP_PRICE } from "./membership";
import { Link } from "react-router-dom";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AddressField from "./AddressField";
import { PageShell, PageHeader, PageContent, Section } from "./Page";

const Publish = () => {
    const [formData, setFormData] = useState({
      title: "",
      description: "",
      price: "",
      city: "",
      direccion: "",
      marca: "",
      modelo: "",
      dni: "",
      patente: "",
      km: "",
      timeToDo: new Date(),
      images: [],
    });
    const [publishSuccess, setPublishSuccess] = useState(false);
    const navigate = useNavigate();
    const { loading: membershipLoading, isActive: hasMembership } = useMembership();
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    };

    const handleAddressChange = (e) => {
      const { value, city } = e.target;
      setFormData((prevFormData) => ({ ...prevFormData, direccion: value, city }));
    };

    const handlePublishProyect = async () => {
      // Validación: comprobar si todos los campos están rellenados
      if (
        !formData.title ||
        !formData.description ||
        !formData.price ||
        !formData.city ||
        !formData.marca ||
        !formData.modelo ||
        !formData.dni ||
        !formData.patente ||
        !formData.km
      ) {
        alert("Por favor, complete todos los campos antes de publicar.");
        return;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const { uid } = currentUser;

      const userDocSnapshot = await getDoc(doc(db, "users", uid));
      const userName = userDocSnapshot.data()?.displayName || currentUser.displayName || "";

      try {
        const carRef = await addDoc(collection(db, "cars"), {
          name: userName,
          title: formData.title,
          description: formData.description,
          price: formData.price,
          city: formData.city,
          direccion: formData.direccion,
          marca: formData.marca,
          modelo: formData.modelo,
          km: formData.km,
          images: formData.images.length > 0 ? formData.images : [],
          createdAt: serverTimestamp(),
          uid,
        });
        // DNI/CUIT y patente: sólo los puede leer el dueño de la publicación
        await setDoc(doc(db, "cars", carRef.id, "private", "documentacion"), {
          dni: formData.dni,
          patente: formData.patente,
        });
        setPublishSuccess(true);
      } catch {
        alert("No se pudo publicar el vehículo. Intentá de nuevo.");
      }
    };

  const handleImageUpload = async (event) => {
    const files = event.target.files; // Get all selected files

    if (!files) {
      return;
    }

    setLoading(true);

    try {
      const imageUrls = []; // Array to store URLs of all uploaded images

      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        images: [...prevFormData.images, ...imageUrls], // Add all new URLs to the array
      }));
    } catch (error) {
      alert(error.message || "No se pudieron subir las imágenes.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      images: prevFormData.images.filter((_, i) => i !== index),
    }));
  };

  const handleRedirectHome = () => {
    navigate("/home");
  };

  const handlePublishClick = async () => {
    await handlePublishProyect();
  };

  if (membershipLoading || !hasMembership) {
    return (
      <PageShell>
        <PageHeader title="Publicar vehículo" />
        <PageContent>
          {membershipLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <EmptyState
              icon={<WorkspacePremiumIcon />}
              title="Necesitás una membresía para publicar"
              text={`Con la membresía de ${MEMBERSHIP_PRICE} por mes publicás todos los vehículos que quieras.`}
              action={
                <Button component={Link} to="/membership" variant="contained" color="secondary">
                  Ver membresía
                </Button>
              }
            />
          )}
        </PageContent>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader title="Publicar vehículo" subtitle="Completá los datos y subí fotos para publicar tu auto" />
      <PageContent>
        <Stack spacing={3}>
          <Section title="Datos del vehículo">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  id="title"
                  label="Título"
                  placeholder="Ej: Volkswagen Golf GTI 2019"
                  fullWidth
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Marca"
                  name="marca"
                  value={formData.marca}
                  onChange={handleInputChange}
                  fullWidth
                >
                  {Marcas.map((marca) => (
                    <MenuItem key={marca} value={marca}>
                      {marca}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="modelo"
                  label="Modelo"
                  fullWidth
                  name="modelo"
                  value={formData.modelo}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="price"
                  label="Precio"
                  fullWidth
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: "text.secondary" }}>$</Box> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="km"
                  label="Kilometraje"
                  fullWidth
                  name="km"
                  value={formData.km}
                  onChange={handleInputChange}
                  InputProps={{ endAdornment: <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>km</Box> }}
                />
              </Grid>
              <Grid item xs={12}>
                <AddressField
                  label="Ubicación del vehículo"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleAddressChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="description"
                  label="Características del vehículo"
                  placeholder="Estado, equipamiento, service, detalles..."
                  fullWidth
                  name="description"
                  multiline
                  rows={5}
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Documentación" description="Estos datos no se muestran en la publicación.">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="dni"
                  label="N° de DNI o CUIL/CUIT"
                  fullWidth
                  name="dni"
                  value={formData.dni}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="patente"
                  label="Patente del vehículo"
                  fullWidth
                  name="patente"
                  value={formData.patente}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Fotos" description="Las publicaciones con buenas fotos reciben más consultas.">
            <Box
              component="label"
              htmlFor="upload-button"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 4,
                border: 2,
                borderStyle: "dashed",
                borderColor: "divider",
                borderRadius: 3,
                bgcolor: "background.default",
                color: "text.secondary",
                cursor: loading ? "progress" : "pointer",
                transition: "border-color .2s",
                "&:hover": { borderColor: "secondary.main" },
              }}
            >
              {loading ? (
                <CircularProgress size={32} color="secondary" />
              ) : (
                <AddPhotoAlternateIcon sx={{ fontSize: 36, color: "secondary.dark" }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                {loading ? "Subiendo imágenes..." : "Subir imágenes del vehículo"}
              </Typography>
            </Box>
            <input
              type="file"
              accept="image/*"
              onClick={(event) => (event.target.value = null)}
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="upload-button"
              multiple // Add the multiple attribute
              disabled={loading}
            />

            {/* Vista previa de las imágenes */}
            {formData.images.length > 0 && (
              <Grid container spacing={1.5} sx={{ mt: 1 }}>
                {formData.images.map((imageUrl, index) => (
                  <Grid item xs={4} sm={3} md={2} key={index}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`Imagen ${index + 1} del vehículo`}
                        sx={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 2, display: "block" }}
                      />
                      <IconButton
                        aria-label="Eliminar imagen"
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(17, 15, 36, 0.7)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(17, 15, 36, 0.9)" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Section>

          {publishSuccess ? (
            <Alert
              severity="success"
              action={
                <Button onClick={handleRedirectHome} color="inherit" size="small">
                  Ir a Inicio
                </Button>
              }
            >
              El anuncio se ha publicado correctamente.
            </Alert>
          ) : (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handlePublishClick}
                sx={{ width: { xs: "100%", sm: "auto" }, px: 5 }}
              >
                Publicar
              </Button>
            </Stack>
          )}
        </Stack>
      </PageContent>
    </PageShell>
  );
};

export default Publish;
